// Booking-scoped and account verification screen. Carries the
// renter through Didit ID-doc capture (with "scan QR to continue on phone"
// fallback) and surfaces every terminal state with a polished, car-themed
// timeline.
//
// Uses a single-flight, server-guided refresh loop. The loop is deliberately
// local to this panel so a second tab cannot create a second provider order.

import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "wouter";
import { motion, AnimatePresence } from "framer-motion";
import { useReducedMotion, entranceProps } from "@/lib/motion";
import { useTranslation } from "react-i18next";
import { licenceActionClass } from "./licence-action";
import { Spinner } from "@/components/ui/spinner";
import {
  Check, AlertCircle, Clock, Search, Shield, Smartphone,
  ShieldCheck, XCircle, AlertTriangle, RefreshCw, ArrowLeft
} from "lucide-react";
import {
  startLicenceVerification,
  fetchLicenceVerificationStatus,
  type LicenceVerificationPhase,
  type LicenceVerificationStatus,
} from "@/lib/users-api";
import { formatBookingShortRef } from "@/lib/booking-ref";

interface Props {
  token: string | null | undefined;
  /** Stable booking context keeps the handoff understandable and prevents
   * provider URLs from becoming the user's navigational destination. */
  publicId?: string;
  awaitingHost?: boolean;
  car?: { name: string; image?: string | null } | null;
  onApproved?: () => void;
  reuploadRequested?: boolean;
  freshLicenceRequired?: boolean;
  bookingVerificationState?: "not_started" | "in_progress" | "in_review" | "action_required" | "failed" | "complete";
  onStarted?: () => void;
}

const TERMINAL_PHASES: ReadonlySet<LicenceVerificationPhase> = new Set([
  "approved",
  "didit_declined",
  "mvr_declined",
  "cancelled",
  "manual_review",
]);

const DEFAULT_POLL_MS = 4_000;
const MIN_POLL_MS = 4_000;
const MAX_POLL_MS = 30_000;

interface SessionState {
  url: string;
  qr: string | null;
  sessionId: string;
  isCurrent?: boolean;
}

export default function LicenceVerificationPanel({
  token,
  publicId,
  awaitingHost = false,
  car,
  onApproved,
  reuploadRequested = false,
  freshLicenceRequired = false,
  bookingVerificationState,
  onStarted,
}: Props) {
  const { t } = useTranslation();
  const reduced = useReducedMotion();
  const [session, setSession] = useState<SessionState | null>(null);
  const [statusReady, setStatusReady] = useState(false);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const [status, setStatus] = useState<LicenceVerificationStatus | null>(null);
  const [polling, setPolling] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const onApprovedFiredRef = useRef(false);
  const scopeKey = `${publicId ?? ""}:${token ?? ""}`;
  const scopeKeyRef = useRef(scopeKey);
  scopeKeyRef.current = scopeKey;
  useEffect(() => {
    onApprovedFiredRef.current = false;
    setStarting(false);
    setStartError(null);
  }, [scopeKey]);
  const timerRef = useRef<number | null>(null);
  const requestRef = useRef<AbortController | null>(null);
  const inFlightRef = useRef(false);
  const lastEventRefreshRef = useRef(0);
  const statusRef = useRef(status);
  const sessionRef = useRef(session);
  const refreshRef = useRef<(immediate: boolean) => Promise<void>>(async () => {});
  const justStartedRef = useRef<{ sessionId: string; at: number } | null>(null);
  statusRef.current = status;
  sessionRef.current = session;

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);

  const pollDelay = useCallback((next: LicenceVerificationStatus, failed = false) => {
    const guided = next.poll_after_ms ?? next.pollAfterMs;
    const guidedAt = next.next_poll_at ?? next.nextPollAt;
    const fromDate = guidedAt ? new Date(guidedAt).getTime() - Date.now() : null;
    const base = typeof guided === "number" && Number.isFinite(guided)
      ? guided
      : fromDate !== null && Number.isFinite(fromDate) ? fromDate : DEFAULT_POLL_MS;
    const bounded = Math.min(MAX_POLL_MS, Math.max(MIN_POLL_MS, failed ? base * 1.5 : base));
    const jittered = bounded * (0.9 + Math.random() * 0.2);
    return Math.round(Math.min(MAX_POLL_MS, Math.max(MIN_POLL_MS, jittered)));
  }, []);

  const scheduleRefresh = useCallback((next: LicenceVerificationStatus, failed = false) => {
    clearTimer();
    const phase = next.phase;
    const awaitingTeamReview = next.licence_verified === true &&
      next.kyc_log?.status === "verified" &&
      !!next.review_reason && !next.review_decision;
    const automaticallyRetryable =
      next.ui_state === "stale" ||
      next.ui_state === "error" ||
      next.retryable === true;
    const inFlight = awaitingTeamReview || ((next.should_poll !== false || automaticallyRetryable)
      && next.terminal !== true
      && !TERMINAL_PHASES.has(phase)
      && (phase !== "not_started" || !!sessionRef.current));
    if (!inFlight || document.visibilityState !== "visible" || !navigator.onLine) return;
    timerRef.current = window.setTimeout(
      () => { void refreshRef.current(false); },
      awaitingTeamReview ? Math.max(15_000, pollDelay(next, failed)) : pollDelay(next, failed),
    );
  }, [clearTimer, pollDelay]);

  const refreshStatus = useCallback(async (immediate: boolean) => {
    if (!publicId || !token || inFlightRef.current || document.visibilityState !== "visible" || !navigator.onLine) return;
    const current = statusRef.current;
    const phase = current?.phase ?? "not_started";
    const awaitingTeamReview = current?.licence_verified === true &&
      current.kyc_log?.status === "verified" &&
      !!current.review_reason && !current.review_decision;
    const automaticallyRetryable =
      current?.ui_state === "stale" ||
      current?.ui_state === "error" ||
      current?.retryable === true;
    if (!immediate && !awaitingTeamReview && (
      TERMINAL_PHASES.has(phase) ||
      current?.terminal === true ||
      (current?.should_poll === false && !automaticallyRetryable)
    )) return;
    if (!immediate && phase === "not_started" && !sessionRef.current) return;
    if (immediate && Date.now() - lastEventRefreshRef.current < 500) return;
    if (immediate) lastEventRefreshRef.current = Date.now();
    inFlightRef.current = true;
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    setPolling(true);
    try {
      // The existing helper does not yet accept AbortSignal, so abort still
      // prevents state updates and cancels the loop on unmount.
      const fetched = await fetchLicenceVerificationStatus(token, publicId);
      if (controller.signal.aborted) return;
      // The start endpoint persists the context before returning, but a
      // read-replica can briefly report not_started. Keep the just-created
      // session during that window, except when the server explicitly retired
      // it: a reset must clear even a URL held in this component's memory.
      const activeSession = sessionRef.current;
      const justStarted = activeSession &&
        justStartedRef.current?.sessionId === activeSession.sessionId &&
        Date.now() - justStartedRef.current.at < 15_000;
      const next: LicenceVerificationStatus =
        fetched.phase === "not_started" && activeSession &&
        (!fetched.retired_attempt ||
          (justStarted && fetched.last_session_id !== activeSession.sessionId))
          ? {
              ...fetched,
              phase: "didit_in_progress",
              terminal: false,
              should_poll: true,
              provider: {
                ...fetched.provider,
                didit_session_id: activeSession.sessionId,
                provider_ref: activeSession.sessionId,
                verification_url: activeSession.url,
              },
            }
          : fetched;
      if (fetched.phase === "didit_in_progress" || fetched.retired_attempt && !justStarted) {
        justStartedRef.current = null;
      }
      setStatus(next);
      statusRef.current = next;
      const resumeUrl = (next.phase === "didit_in_progress" ||
        (next.phase === "not_started" && next.kyc_log?.status === "pending" && !next.retired_attempt))
        ? next.provider?.verification_url
        : null;
      if (resumeUrl) {
        const resumedSession = {
          url: resumeUrl,
          qr: next.provider?.verification_qr ?? null,
          sessionId: next.provider?.didit_session_id ?? next.last_session_id ?? "existing",
          isCurrent: next.session_is_current ?? next.provider?.session_is_current ?? true,
        };
        setSession(resumedSession);
        sessionRef.current = resumedSession;
      } else if (next.phase !== "didit_in_progress") {
        setSession(null);
        sessionRef.current = null;
      }
      setRefreshError(null);
      if ((next.phase === "approved" ||
        (next.licence_verified && next.kyc_log?.status === "verified")) && !onApprovedFiredRef.current) {
        onApprovedFiredRef.current = true;
        onApproved?.();
      }
      scheduleRefresh(next);
    } catch (err) {
      if (!controller.signal.aborted) {
        setRefreshError(err instanceof Error ? err.message : "refresh_failed");
        const fallback = statusRef.current;
        if (fallback) scheduleRefresh(fallback, true);
        else if (document.visibilityState === "visible" && navigator.onLine) {
          // A transient failure on the first read must not strand a desktop
          // QR handoff: the phone can complete before this page has status.
          clearTimer();
          timerRef.current = window.setTimeout(() => { void refreshRef.current(true); }, 10_000);
        }
      }
    } finally {
      if (!controller.signal.aborted) {
        setPolling(false);
        setStatusReady(true);
      }
      inFlightRef.current = false;
    }
  }, [clearTimer, onApproved, publicId, scheduleRefresh, token]);
  refreshRef.current = refreshStatus;

  // Initial status fetch and cleanup. A reload therefore resumes from the
  // server without creating a new Didit session.
  useEffect(() => {
    setSession(null);
    setStatus(null);
    setStatusReady(false);
    if (!publicId || !token) return;
    setStatusReady(false);
    setRefreshError(null);
    void refreshStatus(true);
    return () => {
      clearTimer();
      requestRef.current?.abort();
      requestRef.current = null;
      inFlightRef.current = false;
    };
  }, [clearTimer, refreshStatus, token]);

  // Resume immediately after returning to a visible, online page. Event
  // bursts are deduped by refreshStatus' single-flight guard and timestamp.
  useEffect(() => {
    if (!publicId || !token) return;
    const resume = () => {
      if (document.visibilityState === "visible" && navigator.onLine) void refreshStatus(true);
    };
    const onVisibility = () => resume();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pageshow", resume);
    window.addEventListener("online", resume);
    window.addEventListener("focus", resume);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pageshow", resume);
      window.removeEventListener("online", resume);
      window.removeEventListener("focus", resume);
    };
  }, [refreshStatus, token]);

  async function handleStart() {
    if (!publicId || !token) {
      setStartError(t("verification.licence_no_url"));
      return;
    }
    setStarting(true);
    setStartError(null);
    const startScope = scopeKeyRef.current;
    try {
      const res = await startLicenceVerification(token, publicId);
      if (scopeKeyRef.current !== startScope) return;
      if (!res.verification_url || !res.session_id) {
        setStartError(t("verification.licence_no_url"));
        return;
      }
      const startedSession = {
        url: res.verification_url,
        qr: res.verification_qr,
        sessionId: res.session_id,
        isCurrent: res.session_is_current !== false,
      };
      setSession(startedSession);
      sessionRef.current = startedSession;
      justStartedRef.current = { sessionId: startedSession.sessionId, at: Date.now() };
      onStarted?.();
      const currentStatus = statusRef.current;
      if (currentStatus) {
        const startedStatus: LicenceVerificationStatus = {
          ...currentStatus,
          phase: "didit_in_progress",
          retired_attempt: false,
          terminal: false,
          should_poll: true,
          provider: {
            ...currentStatus.provider,
            didit_session_id: startedSession.sessionId,
            provider_ref: startedSession.sessionId,
            verification_url: startedSession.url,
          },
        };
        setStatus(startedStatus);
        statusRef.current = startedStatus;
        scheduleRefresh(startedStatus);
      }
      // Refresh status right after starting so the timeline updates.
      await refreshStatus(true);
    } catch (err) {
      if (scopeKeyRef.current !== startScope) return;
      setStartError(err instanceof Error ? err.message : t("verification.licence_no_url"));
      // A concurrent attempt or an admin change can make the allowance stale
      // between the initial status read and this atomic start claim.
      await refreshStatus(true);
    } finally {
      if (scopeKeyRef.current === startScope) setStarting(false);
    }
  }

  if (!publicId) {
    return <p className="text-muted-foreground">
      {t("booking_verification_access.eligible_booking")}
    </p>;
  }

  if (!statusReady) {
    return (
      <div className="mt-7 min-h-48 rounded-2xl border border-border bg-card p-6"
        role="status" aria-live="polite" aria-busy="true" data-testid="licence-verification-initial-loading">
        <span className="sr-only">{t("common.loading")}</span>
        <div className="h-5 w-36 rounded bg-muted" aria-hidden="true" />
        <div className="mt-5 h-4 w-3/4 rounded bg-muted" aria-hidden="true" />
        <div className="mt-3 h-4 w-1/2 rounded bg-muted" aria-hidden="true" />
      </div>
    );
  }

  const phase: LicenceVerificationPhase = status?.phase ?? (session ? "didit_in_progress" : "not_started");
  const uiState = status?.ui_state;
  const providerDiditUrl = session?.url ?? status?.provider?.verification_url ?? null;
  const sessionRetired =
    session?.isCurrent === false ||
    status?.session_is_current === false ||
    status?.provider?.session_is_current === false ||
    !!status?.retired_attempt;
  // A stale provider result does not invalidate an authenticated, resumable
  // Didit URL. Match the Expo recovery flow: let the renter finish the
  // existing paid attempt first, and reserve the generic stale state for
  // records where there is genuinely nothing actionable to resume.
  const resumableDiditUrl =
    providerDiditUrl && !sessionRetired &&
    (phase === "didit_in_progress" || uiState === "stale" ||
      (phase === "not_started" && status?.kyc_log?.status === "pending"))
      ? providerDiditUrl
      : null;
  const currentDiditSession =
    !!resumableDiditUrl &&
    !sessionRetired &&
    (phase === "didit_in_progress" || (phase === "not_started" && status?.kyc_log?.status === "pending")) &&
    !status?.retired_attempt;
  // Didit's `pending` log means the paid session exists but the renter has
  // not begun the provider flow. A resumable URL alone must not make this
  // look like an in-flight verification (the detail summary calls it
  // "Not started"). Keep the URL/action so the renter can continue the same
  // session, never mint a second one. Once the provider log is in_progress
  // or review, preserve that authoritative state.
  const pendingResumable =
    !!resumableDiditUrl &&
    (phase === "didit_in_progress" || phase === "not_started") &&
    status?.kyc_log?.status?.trim().toLowerCase() === "pending";
  const idCheckComplete = status?.licence_verified === true && status.kyc_log?.status === "verified";
  const providerDisplayPhase: LicenceVerificationPhase =
    idCheckComplete && (phase === "didit_in_review" || phase === "manual_review" ||
      uiState === "manual_review" || uiState === "error")
      ? "mvr_review"
    : resumableDiditUrl
    ? pendingResumable ? "not_started" : "didit_in_progress"
    : phase === "stale" || uiState === "stale"
      ? "stale"
      : uiState === "manual_review"
        ? "manual_review"
        : uiState === "error"
          ? "error"
          : phase;
  // The account-wide licence flag can remain approved after retained evidence
  // is cleared for a booking-specific re-check. Never present the full green
  // timeline for that booking while its server summary requires fresh Didit.
  const displayPhase: LicenceVerificationPhase =
    providerDisplayPhase === "approved" && (reuploadRequested || (publicId && (freshLicenceRequired ||
      (bookingVerificationState != null && bookingVerificationState !== "complete"))))
      ? reuploadRequested || bookingVerificationState === "not_started"
        ? "not_started"
        : bookingVerificationState === "in_review"
          ? freshLicenceRequired || reuploadRequested ? "manual_review" : "mvr_review"
          : bookingVerificationState === "in_progress"
            ? freshLicenceRequired || reuploadRequested ? "didit_in_progress" : "mvr_in_progress"
            : "not_started"
      : providerDisplayPhase;
  const licenceCompleteAwaitingHost = awaitingHost &&
    status?.licence_verified === true &&
    !status.mvr_status &&
    !status.review_reason &&
    !reuploadRequested &&
    (!freshLicenceRequired || status.kyc_log?.status === "verified");
  const diditRetryable =
    phase === "didit_declined" &&
    status?.retryable === true &&
    (status?.attempts_remaining == null || status.attempts_remaining > 0);
  const attemptsCopy =
    status?.attempts_remaining != null && status?.max_attempts != null
      ? t("verification.panel.attempts_remaining", "{{remaining}} of {{maximum}} attempts remaining", {
          remaining: status.attempts_remaining,
          maximum: status.max_attempts,
        })
      : null;

  return (
    <div
      className="mt-7 w-full scroll-mt-24"
      data-testid="panel-licence-verification"
      aria-busy={polling}
    >
      {publicId && (
        <Link
          href={`/trips/${encodeURIComponent(publicId)}`}
          data-testid="link-verification-back-to-booking"
          className={`mb-5 ${licenceActionClass}`}
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          {t("conversation.back_to_booking")}
        </Link>
      )}
      {car && (
        <div className="mb-5 flex items-center gap-4 rounded-2xl border border-border bg-card p-4 sm:p-5" data-testid="verification-booking-context">
          {car.image ? (
            <img src={car.image} alt={car.name} className="h-20 w-24 shrink-0 rounded-xl object-cover sm:w-28" />
          ) : (
            <div className="h-20 w-24 shrink-0 rounded-xl bg-muted sm:w-28" aria-hidden="true" />
          )}
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("verification.panel.booking_context")}</p>
            <p className="mt-1 truncate text-base font-semibold text-foreground sm:text-lg">{car.name}</p>
            {publicId && <p className="mt-1 truncate text-sm text-muted-foreground">{t("verification.panel.booking_reference", { reference: formatBookingShortRef(publicId) })}</p>}
          </div>
        </div>
      )}
      <div
        role={refreshError ? "alert" : "status"}
        aria-live={refreshError ? "assertive" : "polite"}
        data-testid="status-licence-verification"
        className="sr-only"
      >
        {refreshError
          ? t("verification.panel.refresh_error")
          : displayPhase === "stale" || displayPhase === "error"
            ? t("verification.panel.checking_automatically")
          : licenceCompleteAwaitingHost
            ? t("verification.panel.ready_for_host_title")
            : t(`verification.panel.phase_${displayPhase}`)}
      </div>
      <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
        <Timeline phase={displayPhase} t={t} />
        <AnimatePresence key={status?.retired_attempt ? "retired-attempt" : "active-attempt"} initial={false}>
        {displayPhase === "approved" ? (
          <PhaseShell key="approved" tone="success" reduced={reduced}>
            <PhaseTitle tone="success" icon={ShieldCheck}>{t("verification.panel.approved_title")}</PhaseTitle>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{t("verification.panel.approved_desc")}</p>
          </PhaseShell>
        ) : displayPhase === "cancelled" ? (
          <PhaseShell key="cancelled" tone="error" reduced={reduced}>
            <PhaseTitle tone="error" icon={XCircle}>{t("verification.panel.cancelled_title")}</PhaseTitle>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{status?.message || t("verification.panel.cancelled_desc")}</p>
          </PhaseShell>
        ) : displayPhase === "stale" ? (
          <PhaseShell key="stale" tone="info" reduced={reduced}>
            <PhaseTitle tone="info" icon={Clock}>{t("verification.panel.stale_title")}</PhaseTitle>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{t("verification.panel.stale_desc")}</p>
            {polling && <div className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-muted-foreground"><Spinner size="sm" />{t("verification.panel.checking_automatically")}</div>}
          </PhaseShell>
        ) : displayPhase === "manual_review" ? (
          <PhaseShell key="manual-review" tone="info" reduced={reduced}>
            <PhaseTitle tone="info" icon={Search}>{t("verification.panel.manual_review_title")}</PhaseTitle>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{status?.message || t("verification.panel.manual_review_desc")}</p>
          </PhaseShell>
        ) : displayPhase === "error" || (refreshError && !idCheckComplete) ? (
          <PhaseShell key="refresh-error" tone="error" reduced={reduced}>
            <PhaseTitle tone="error" icon={AlertTriangle}>{t("verification.panel.refresh_error_title")}</PhaseTitle>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{t("verification.panel.refresh_error_desc")}</p>
            <button type="button" onClick={() => void refreshStatus(true)} disabled={polling || !token}
              className={`${verificationActionClass} mt-5`}
              data-testid="button-licence-refresh-status">
              {polling && <Spinner size="sm" className="text-white" />}
              {t("verification.try_again")}
            </button>
          </PhaseShell>
        ) : displayPhase === "didit_declined" || displayPhase === "mvr_declined" ? (
          <PhaseShell key="declined" tone="error" reduced={reduced}>
            <PhaseTitle tone="error" icon={AlertCircle}>{t("verification.panel.declined_title")}</PhaseTitle>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
              {status?.message || t("verification.panel.declined_desc")}
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-4">
              {diditRetryable && (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      onApprovedFiredRef.current = false;
                      setSession(null);
                      setStatus(null);
                      void handleStart();
                    }}
                    disabled={starting || !token}
                    data-testid="button-licence-try-again"
                     className={verificationActionClass}
                  >
                    {starting ? <Spinner size="sm" className="text-white dark:text-primary-foreground" /> : <RefreshCw className="h-4 w-4" />}
                    {t("verification.panel.try_again")}
                  </button>
                  {attemptsCopy ? (
                    <p className="w-full text-sm text-muted-foreground" data-testid="text-licence-attempts-remaining">
                      {attemptsCopy}
                    </p>
                  ) : null}
                </>
              )}
              {phase === "didit_declined" && !diditRetryable && (
                <p className="w-full text-sm font-medium text-muted-foreground" data-testid="text-licence-attempts-exhausted">
                  {attemptsCopy
                    ? t("verification.panel.attempts_exhausted", "{{attempts}}. Please contact support for help.", { attempts: attemptsCopy })
                    : t("verification.panel.contact_support_desc", "Please contact support for help with this verification.")}
                  {publicId && (
                    <span className="mt-2 block" data-testid="verification-support-booking-reference">
                      {t("verification.panel.attempts_support_booking", {
                        reference: formatBookingShortRef(publicId),
                      })}
                    </span>
                  )}
                </p>
              )}
              <Link
                href="/help"
                className="inline-flex h-11 items-center justify-center px-4 text-sm font-semibold text-brand-deep underline decoration-brand-deep/30 underline-offset-4 transition-colors hover:decoration-brand-deep/100 dark:text-primary dark:decoration-primary/30 dark:hover:decoration-primary/100"
              >
                {t("verification.panel.contact_support")}
              </Link>
            </div>
          </PhaseShell>
        ) : displayPhase === "didit_in_review" || displayPhase === "mvr_review" ? (
          <PhaseShell key="review" tone="info" reduced={reduced}>
            <PhaseTitle tone="info" icon={Search}>
              {idCheckComplete ? t("verification.panel.mvr_title") : t("verification.panel.review_title")}
            </PhaseTitle>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
              {idCheckComplete ? t("verification.panel.mvr_desc") : status?.message || t("verification.panel.review_desc")}
            </p>
          </PhaseShell>
        ) : displayPhase === "mvr_in_progress" && licenceCompleteAwaitingHost ? (
          <PhaseShell key="ready-for-host" tone="success" reduced={reduced}>
            <PhaseTitle tone="success" icon={ShieldCheck}>{t("verification.panel.ready_for_host_title")}</PhaseTitle>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{t("verification.panel.ready_for_host_desc")}</p>
          </PhaseShell>
        ) : displayPhase === "mvr_in_progress" ? (
          <PhaseShell key="mvr" tone="info" reduced={reduced}>
            <PhaseTitle tone="info">
              <Spinner size="sm" className="text-brand-deep dark:text-primary" />
              {uiState === "delayed" ? t("verification.panel.stale_title") : t("verification.panel.mvr_title")}
            </PhaseTitle>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{t("verification.panel.mvr_desc")}</p>
          </PhaseShell>
        ) : !statusReady ? (
          <PhaseShell key="status-loading" tone="brand" reduced={reduced}>
            <PhaseTitle tone="brand">
              <Spinner size="sm" className="text-brand-deep dark:text-primary" />
              {t("verification.panel.in_progress_title")}
            </PhaseTitle>
          </PhaseShell>
           ) : resumableDiditUrl ? (
          <PhaseShell key="didit-progress" tone="brand" reduced={reduced}>
              <PhaseTitle tone="brand">{pendingResumable ? t("verification.panel.start_title") : t("verification.panel.in_progress_title")}</PhaseTitle>
             <p className="mt-2 text-base leading-relaxed text-foreground/75">
                {pendingResumable ? t("verification.panel.start_desc") : t("verification.panel.resume_existing_note")}
            </p>
            <a
              href={resumableDiditUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="link-licence-resume"
               className={`${verificationActionClass} mt-5 ${session?.qr && currentDiditSession ? "md:hidden" : ""}`}
            >
              <Smartphone className="h-4 w-4" />
              {t("verification.panel.continue_check")}
            </a>
             {pendingResumable && (
               <p className="mt-4 text-sm leading-relaxed text-foreground/75">
                 {t("verification.panel.resume_existing_note")}
               </p>
             )}
            {session?.qr && currentDiditSession && (
               <div className="mt-5 hidden flex-col gap-5 rounded-xl border border-border bg-secondary/30 p-5 md:flex md:flex-row md:items-center" data-testid="licence-verification-current-session-qr">
                 <div className="shrink-0 overflow-hidden rounded-lg border border-border bg-white p-2">
                  <img
                    src={session.qr}
                    alt={t("verification.panel.qr_alt")}
                     className="h-36 w-36"
                    data-testid="img-licence-qr"
                  />
                </div>
                <div>
                   <p className="text-base font-semibold text-foreground">{t("verification.panel.qr_title")}</p>
                   <p className="mt-2 text-sm leading-relaxed text-foreground/75">{t("verification.panel.qr_desc")}</p>
                </div>
              </div>
            )}
          </PhaseShell>
        ) : displayPhase === "didit_in_progress" ? (
          <PhaseShell key="didit-progress-no-url" tone="error" reduced={reduced}>
            <PhaseTitle tone="error" icon={AlertTriangle}>{t("verification.panel.refresh_error_title")}</PhaseTitle>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{t("verification.licence_no_url")}</p>
            <button
              type="button"
              onClick={() => void handleStart()}
              disabled={starting || polling || !token}
              data-testid="button-licence-continue-existing"
               className={`${verificationActionClass} mt-5`}
            >
              {(starting || polling) && <Spinner size="sm" className="text-white" />}
              {t("verification.panel.continue_check")}
            </button>
          </PhaseShell>
        ) : (
          <PhaseShell key="start" tone="brand" reduced={reduced}>
            <PhaseTitle tone="brand" icon={Shield}>{t("verification.panel.start_title")}</PhaseTitle>
             <p className="mt-2 text-base leading-relaxed text-foreground/75">{t("verification.panel.start_desc")}</p>
            {status?.attempts_remaining === 0 ? (
              <div role="status" data-testid="verification-booking-attempts-exhausted" className="mt-5 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
                <p>{attemptsCopy
                  ? t("verification.panel.attempts_exhausted", { attempts: attemptsCopy })
                  : t("verification.panel.contact_support_desc", "Please contact support for help with this verification.")}</p>
                {publicId && (
                  <p className="mt-2" data-testid="verification-support-booking-reference">
                    {t("verification.panel.attempts_support_booking", {
                      reference: formatBookingShortRef(publicId),
                    })}
                  </p>
                )}
                <Link href="/help" className="mt-3 inline-block font-semibold underline underline-offset-4">
                  {t("common.contact_support")}
                </Link>
              </div>
            ) : (
            <button
              type="button"
              onClick={handleStart}
              disabled={starting || !token}
              data-testid="button-start-licence-verification"
               className={`${verificationActionClass} mt-6`}
            >
              {starting && <Spinner size="sm" className="text-white dark:text-primary-foreground" />}
              {starting ? t("verification.starting") : t("booking.cta_start_verification")}
            </button>
            )}
            {startError && status?.attempts_remaining !== 0 && (
              <div
                role="alert"
                data-testid="text-licence-verification-error"
                className="mt-4 flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-900 border border-amber-200/50 dark:bg-amber-950/30 dark:border-amber-900/50 dark:text-amber-200"
              >
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <p>
                  {startError}{" "}
                  <Link
                    href="/help/contact"
                    data-testid="link-licence-verification-fallback"
                    className="font-semibold underline decoration-amber-900/30 underline-offset-4 hover:decoration-amber-900/100 dark:decoration-amber-200/30 dark:hover:decoration-amber-200/100"
                  >
                    {t("common.contact_support")}
                  </Link>
                </p>
              </div>
            )}
          </PhaseShell>
        )}
        </AnimatePresence>
      </div>
    </div>
  );
}

const verificationActionClass = licenceActionClass;

function PhaseShell({ tone, reduced, children }: { tone: "brand" | "success" | "error" | "info"; reduced: boolean; children: React.ReactNode }) {
  const toneClass =
    tone === "success" ? "bg-brand-tint/40 dark:bg-primary/10"
    : tone === "error" ? "bg-amber-50/60 dark:bg-amber-950/20"
    : tone === "info" ? "bg-secondary/30"
    : "bg-card";

  return (
    <motion.div
      {...entranceProps(reduced, { y: 10 })}
      exit={reduced ? { opacity: 0 } : { opacity: 0, y: -8 }}
      className={`border-t border-border/70 ${toneClass} transition-colors duration-300`}
    >
      <div className="p-5 sm:p-6">{children}</div>
    </motion.div>
  );
}

function PhaseTitle({ tone, icon: Icon, children }: { tone: "brand" | "success" | "error" | "info"; icon?: React.ElementType; children: React.ReactNode }) {
  const toneText =
    tone === "success" ? "text-brand-deep dark:text-primary"
    : tone === "error" ? "text-amber-900 dark:text-amber-500"
    : tone === "info" ? "text-foreground"
    : "text-foreground";

  return (
    <h2 className={`flex items-center gap-2.5 font-sans text-lg font-semibold tracking-tight ${toneText}`}>
      {Icon && <Icon className="h-5 w-5 shrink-0" strokeWidth={2.5} />}
      {children}
    </h2>
  );
}

interface TimelineStep {
  key: string;
  label: string;
  state: "done" | "current" | "pending" | "error";
}

function Timeline({ phase, t }: { phase: LicenceVerificationPhase; t: (k: string) => string }) {
  const steps: TimelineStep[] = (() => {
    const empty: TimelineStep[] = [
      { key: "id", label: t("verification.panel.step_id"), state: "pending" },
      { key: "mvr", label: t("verification.panel.step_mvr"), state: "pending" },
      { key: "approved", label: t("verification.panel.step_approved"), state: "pending" },
    ];
    switch (phase) {
      case "not_started":
        return [{ ...empty[0], state: "current" }, empty[1], empty[2]];
      case "didit_in_progress":
      case "didit_in_review":
        return [
          { ...empty[0], state: "current" },
          empty[1],
          empty[2],
        ];
      case "didit_declined":
        return [{ ...empty[0], state: "error" }, empty[1], empty[2]];
      case "mvr_in_progress":
        return [{ ...empty[0], state: "done" }, { ...empty[1], state: "current" }, empty[2]];
      case "mvr_review":
        return [{ ...empty[0], state: "done" }, { ...empty[1], state: "current" }, empty[2]];
      case "mvr_declined":
        return [{ ...empty[0], state: "done" }, { ...empty[1], state: "error" }, empty[2]];
      case "approved":
        return [
          { ...empty[0], state: "done" },
          { ...empty[1], state: "done" },
          { ...empty[2], state: "done" },
        ];
      case "cancelled":
      case "error":
      case "manual_review":
      case "stale":
        return [{ ...empty[0], state: "error" }, empty[1], empty[2]];
    }
  })();

  return (
    <ol className="grid w-full grid-cols-3 gap-0 px-4 py-5 sm:px-6" data-testid="list-licence-timeline">
      {steps.map((s, idx) => {
        const isDone = s.state === "done";
        const isCurrent = s.state === "current";
        const isError = s.state === "error";

        return (
          <li key={s.key} aria-current={isCurrent ? "step" : undefined} className="relative flex min-w-0 flex-col items-center gap-2 text-center">
            {idx < steps.length - 1 && (
              <span aria-hidden="true" className={`absolute left-1/2 right-[-50%] top-[15px] h-0.5 ${isDone ? "bg-brand dark:bg-primary" : "bg-border"}`} />
            )}
            <span
              className={`relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-xs font-semibold ring-4 ring-card ${
                isDone || isCurrent
                  ? "border-brand-deep bg-brand-deep text-white dark:border-primary dark:bg-primary dark:text-primary-foreground"
                  : isError
                    ? "border-destructive bg-destructive text-destructive-foreground"
                    : "border-border bg-card text-foreground/75"
              }`}
            >
              {isDone ? <Check className="h-4 w-4" strokeWidth={2.5} /> : isError ? <AlertTriangle className="h-3.5 w-3.5" strokeWidth={2.5} /> : idx + 1}
            </span>
            <span
              className={`max-w-full text-xs leading-snug sm:text-sm ${
                isDone || isCurrent
                  ? "font-semibold text-foreground"
                  : isError
                    ? "font-semibold text-destructive"
                    : "font-medium text-foreground/75"
              }`}
            >
              {s.label}
            </span>
          </li>
        );
      })}
    </ol>
  );
}
